/* Andariego: autoplay silencioso; sonido solo por elección del visitante. */
(() => {
  'use strict';
  if (window.agMotionInstalled) { window.agMotionRefresh?.(); return; }
  window.agMotionInstalled = true;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const roots = new WeakMap();
  const enhancedTracks = new WeakSet();
  const observed = new WeakSet();
  const activeAnimations = new Set();
  const cleanup = new WeakMap();
  let heroConfig;
  const canMove = () => !reduced.matches && heroConfig?.dataset.motion !== 'false';
  const animateEntry = (el, delay = 0) => {
    if (!canMove() || !el.animate) return;
    const animation = el.animate([
      { opacity: 0.7, transform: 'translateY(16px)' },
      { opacity: 1, transform: 'translateY(0)' }
    ], { duration: 600, delay, easing: 'cubic-bezier(.18,.7,.2,1)', fill: 'none' });
    activeAnimations.add(animation);
    animation.onfinish = () => activeAnimations.delete(animation);
    animation.oncancel = () => activeAnimations.delete(animation);
  };
  const reveal = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      animateEntry(entry.target);
      reveal.unobserve(entry.target);
    }
  }, { threshold: 0.1 }) : null;
  function installVideo(root) {
    if (roots.has(root)) return;
    const video = root.querySelector('video');
    const button = root.querySelector('[data-ag-video-toggle]');
    if (!video || !button) return;
    const soundButton = root.querySelector('[data-ag-sound-toggle]');
    const soundText = soundButton?.querySelector('[data-ag-sound-label]');
    const mutedIcon = soundButton?.querySelector('[data-ag-sound-off-icon]');
    const audibleIcon = soundButton?.querySelector('[data-ag-sound-on-icon]');
    const abort = new AbortController();
    const opt = { signal: abort.signal };
    const state = { manualPaused: false, manualPlay: false, onScreen: true, blocked: false, playRequest: 0 };
    const label = button.querySelector('[data-ag-video-label]');
    const symbol = button.querySelector('[data-ag-video-symbol]');
    const update = () => {
      const action = video.paused ? (root.dataset.playLabel || 'Reproducir video') : (root.dataset.pauseLabel || 'Pausar video');
      if (label) label.textContent = action;
      if (symbol) symbol.textContent = video.paused ? '▶' : 'Ⅱ';
      button.setAttribute('aria-label', action);
      button.setAttribute('aria-pressed', String(!video.paused));
      const silent = video.muted || video.volume === 0;
      if (soundButton) {
        const soundAction = silent ? (root.dataset.unmuteLabel || 'Activar sonido') : (root.dataset.muteLabel || 'Silenciar');
        if (soundText) soundText.textContent = soundAction;
        soundButton.setAttribute('aria-label', soundAction);
        soundButton.setAttribute('title', soundAction);
        soundButton.dataset.muted = String(silent);
        if (mutedIcon) mutedIcon.toggleAttribute('hidden', !silent);
        if (audibleIcon) audibleIcon.toggleAttribute('hidden', silent);
      }
      const legacySound = button.querySelector('.ag-cinematic__sound');
      if (legacySound && soundButton) legacySound.hidden = true;
    };
    const play = () => {
      if (!root.isConnected || abort.signal.aborted) return;
      const request = ++state.playRequest;
      root.classList.remove('is-still');
      const failed = error => {
        if (abort.signal.aborted || request !== state.playRequest) return;
        if (error?.name !== 'AbortError' && video.paused) state.blocked = true;
        update();
      };
      // No volver a silenciar aquí: se conserva la elección al pausar o volver al video.
      try { const attempt = video.play(); if (attempt?.catch) attempt.catch(failed); }
      catch (error) { failed(error); }
    };
    const policy = () => {
      const saveData = Boolean(navigator.connection?.saveData);
      const pauseForPreference = (reduced.matches || saveData) && !state.manualPlay;
      if (pauseForPreference) { video.pause(); root.classList.add('is-still'); }
      else if (document.hidden || !state.onScreen || state.manualPaused) { video.pause(); }
      else if (!state.blocked && (root.dataset.autoplay === 'true' || state.manualPlay)) { play(); }
      update();
    };
    video.muted = true; video.defaultMuted = true; video.loop = true; video.playsInline = true;
    button.addEventListener('click', () => {
      if (video.paused) { state.manualPaused = false; state.manualPlay = true; state.blocked = false; play(); }
      else { state.manualPaused = true; state.manualPlay = false; video.pause(); }
      update();
    }, opt);
    soundButton?.addEventListener('click', () => {
      const enable = video.muted || video.volume === 0;
      // La activación se hace directamente dentro del toque/clic, también en móviles.
      video.muted = !enable;
      if (enable) {
        if (video.volume === 0) video.volume = 1;
        state.manualPaused = false; state.manualPlay = true; state.blocked = false;
        play();
      }
      update();
    }, opt);
    ['play', 'playing', 'pause', 'ended', 'error', 'volumechange'].forEach(event => video.addEventListener(event, update, opt));
    video.addEventListener('playing', () => {
      if (document.hidden || !state.onScreen || state.manualPaused || ((reduced.matches || navigator.connection?.saveData) && !state.manualPlay)) video.pause();
    }, opt);
    document.addEventListener('visibilitychange', policy, opt);
    reduced.addEventListener('change', policy, opt);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      state.onScreen = entries[0].isIntersecting; policy();
    }, { threshold: 0.12 }) : null;
    observer?.observe(root);
    roots.set(root, state);
    cleanup.set(root, () => { abort.abort(); video.pause(); observer?.disconnect(); roots.delete(root); });
    video.controls = false;
    button.hidden = false;
    if (soundButton) soundButton.hidden = false;
    policy();
  }
  function enhanceTrack(track) {
    if (enhancedTracks.has(track) || !track.children.length) return;
    enhancedTracks.add(track);
    const section = track.closest('.ag-section');
    if (!section) return;
    const heading = section.querySelector('h2');
    if (!track.id) track.id = 'ag-track-' + (heading?.id || Math.random().toString(36).slice(2));
    track.setAttribute('role', 'region');
    track.tabIndex = 0;
    if (heading?.id) track.setAttribute('aria-labelledby', heading.id);
    const controls = document.createElement('div');
    controls.className = 'ag-strip-controls';
    controls.hidden = true;
    const make = (text, sign) => {
      const b = document.createElement('button'); b.type = 'button'; b.textContent = sign;
      b.setAttribute('aria-label', text); b.setAttribute('aria-controls', track.id);
      controls.append(b); return b;
    };
    const prev = make(heroConfig.dataset.prevLabel, '←');
    const next = make(heroConfig.dataset.nextLabel, '→');
    track.before(controls);
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      controls.hidden = max < 4;
      prev.disabled = track.scrollLeft <= 3;
      next.disabled = track.scrollLeft >= max - 3;
    };
    const move = direction => {
      const child = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: direction * (child.getBoundingClientRect().width + gap), behavior: canMove() ? 'smooth' : 'auto' });
    };
    prev.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', update, { passive: true });
    track.addEventListener('keydown', e => {
      if (e.target !== track || !['ArrowLeft','ArrowRight'].includes(e.key)) return;
      e.preventDefault(); move(e.key === 'ArrowRight' ? 1 : -1);
    });
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(update); ro.observe(track);
      cleanup.set(track, () => { ro.disconnect(); enhancedTracks.delete(track); controls.remove(); });
    }
    update();
  }
  function init() {
    heroConfig = document.querySelector('.ag-cinematic--hero');
    if (!heroConfig) return;
    document.body.classList.add('ag-motion-enabled');
    document.querySelectorAll('[data-ag-cinematic]').forEach(installVideo);
    document.querySelectorAll('.ag-reviews, .ag-section--products .ag-grid:not(.ag-grid--packs)').forEach(enhanceTrack);
    document.querySelectorAll('.ag-section-heading, .ag-feature__copy, .ag-cinematic__spot-copy, .ag-cinematic__photo').forEach(el => {
      if (observed.has(el)) return;
      observed.add(el); reveal?.observe(el);
    });
    if (!observed.has(heroConfig)) {
      observed.add(heroConfig);
      heroConfig.querySelectorAll('.ag-eyebrow, h1, .ag-prose, .ag-actions, .ag-cinematic__benefits').forEach((el, i) => animateEntry(el, i * 70));
    }
  }
  window.agMotionRefresh = init;
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', e => {
    e.target.querySelectorAll('[data-ag-cinematic], .ag-reviews, .ag-grid').forEach(el => cleanup.get(el)?.());
  });
  reduced.addEventListener('change', () => { if (reduced.matches) activeAnimations.forEach(animation => animation.cancel()); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
